/**
 * Offline high-quality render (spec 32).
 *
 * Rebuilds the entire chain inside an OfflineAudioContext and renders faster
 * than real time. Because nothing here has a deadline, the compiler is asked
 * for maximum-quality settings regardless of what the live engine is using:
 * higher oversampling, longer FIRs, more biquad sections.
 *
 * The render uses the SAME worklets as live playback. That matters: an offline
 * path with its own DSP would be a different product that happens to share a
 * UI, and there would be no reason to believe the export matched what was
 * auditioned.
 */

import type { AmpProfile, DacProfile, OutputProfile } from '../types/hardware';
import type { EngineSettings, SystemChain } from '../types/system';
import type { CompiledSystem } from '../types/compiled';
import { compileSystem } from '../dsp/compile';
import { WORKLET_FILES } from '../audio/graph';

export interface OfflineRenderRequest {
  buffer: AudioBuffer;
  chain: SystemChain;
  dac: DacProfile | null;
  amp: AmpProfile | null;
  output: OutputProfile | null;
  engine: EngineSettings;
  sourceRate: number;
  /** Render only this range, in seconds. */
  startSec?: number;
  endSec?: number;
  /** Output sample rate. Defaults to the source buffer's rate. */
  targetRate?: number;
  /** Master gain in dB applied to the render. */
  masterGainDb?: number;
  /** Include the safety limiter. Off by default for a render. */
  limiter?: boolean;
  applySensitivity?: boolean;
  referenceSpl?: number;
  /**
   * Cable resolver. Passed straight through to the compiler so an offline
   * render uses the SAME cabling as live playback -- an export that quietly
   * dropped the speaker cable would not be the chain that was auditioned.
   */
  resolveCable?: (id: string | null) => import('../types/hardware').CableProfile | null;
  onProgress?: (stage: string, fraction: number) => void;
  signal?: AbortSignal;
}

export interface OfflineRenderResult {
  channels: Float32Array[];
  sampleRate: number;
  durationSec: number;
  compiled: CompiledSystem;
  /** Peak of the render, dBFS. */
  peakDbFS: number;
  clipped: boolean;
  settingsUsed: {
    oversample: number;
    outputTaps: number;
    realism: string;
    performance: string;
  };
  renderMs: number;
}

/** Engine settings forced to their highest-fidelity values for offline work. */
export function maximumQualitySettings(engine: EngineSettings): EngineSettings {
  return {
    ...engine,
    realism: 'maximum',
    performance: 'maximum-quality',
    oversample: engine.oversample === 'auto' ? 'auto' : engine.oversample,
    safetyLimiter: false,
  };
}

export async function renderOffline(req: OfflineRenderRequest): Promise<OfflineRenderResult> {
  const t0 = performance.now();
  const src = req.buffer;
  const rate = req.targetRate ?? src.sampleRate;
  const startSec = Math.max(0, req.startSec ?? 0);
  const endSec = Math.min(src.duration, req.endSec ?? src.duration);
  if (endSec <= startSec) throw new Error('The selected render range is empty.');

  const durationSec = endSec - startSec;
  // Extra tail so the FIRs, reverb and any decaying nonlinearity can settle
  // instead of being cut off mid-decay.
  const tailSec = req.chain.room.enabled ? Math.max(1.5, req.chain.room.rt60 * 1.5) : 0.25;
  const totalFrames = Math.ceil((durationSec + tailSec) * rate);

  if (typeof OfflineAudioContext === 'undefined') {
    throw new Error('This browser does not provide OfflineAudioContext, so offline rendering is unavailable.');
  }

  req.onProgress?.('Preparing', 0);
  const ctx = new OfflineAudioContext({
    numberOfChannels: 2,
    length: totalFrames,
    sampleRate: rate,
  });

  for (const f of WORKLET_FILES) {
    await ctx.audioWorklet.addModule(f);
    if (req.signal?.aborted) throw new Error('Render cancelled.');
  }

  const settings = maximumQualitySettings(req.engine);
  const compiled = compileSystem({
    chain: req.chain,
    dac: req.dac,
    amp: req.amp,
    output: req.output,
    engine: settings,
    fs: rate,
    sourceRate: req.sourceRate,
    referenceSpl: req.referenceSpl,
    applySensitivity: req.applySensitivity,
    resolveCable: req.resolveCable,
  });

  req.onProgress?.('Building graph', 0.05);

  const opts = (stageName: string): AudioWorkletNodeOptions => ({
    numberOfInputs: 1,
    numberOfOutputs: 1,
    outputChannelCount: [2],
    channelCount: 2,
    channelCountMode: 'explicit',
    processorOptions: { stageName },
  });

  const dacNode = new AudioWorkletNode(ctx, 'dac-stage', opts('dac'));
  const ampNode = new AudioWorkletNode(ctx, 'amp-stage', opts('amp'));
  const outNode = new AudioWorkletNode(ctx, 'output-stage', opts('output'));
  const spatialNode = new AudioWorkletNode(ctx, 'spatial-stage', opts('spatial'));

  // transitionMs 0: an offline render must start already configured, not fade
  // its hardware in over the first 25 ms.
  // Cloned rather than transferred, for the same reason as the live graph: the
  // returned CompiledSystem stays readable so the caller can report what ran.
  const send = (node: AudioWorkletNode, payload: unknown) =>
    node.port.postMessage({ type: 'profile', payload, transitionMs: 0 });
  send(dacNode, compiled.dac);
  send(ampNode, compiled.amp);
  send(outNode, compiled.output);
  send(spatialNode, compiled.spatial);

  ampNode.port.postMessage({ type: 'param', name: 'volume', value: req.chain.amp.volume });
  outNode.port.postMessage({ type: 'param', name: 'balanceDb', value: req.chain.headphone.balanceDb });

  const bypass = req.chain.bypass;
  dacNode.port.postMessage({ type: 'bypass', value: bypass.dacConversion });
  ampNode.port.postMessage({ type: 'bypass', value: bypass.ampNonlinear });
  outNode.port.postMessage({ type: 'bypass', value: bypass.speaker });
  spatialNode.port.postMessage({ type: 'bypass', value: bypass.room && bypass.spatial });

  const source = ctx.createBufferSource();
  source.buffer = src;

  const master = ctx.createGain();
  master.gain.value = Math.pow(10, (req.masterGainDb ?? 0) / 20);

  source.connect(dacNode);
  dacNode.connect(ampNode);
  ampNode.connect(outNode);
  outNode.connect(spatialNode);
  spatialNode.connect(master);

  if (req.limiter) {
    const lim = new AudioWorkletNode(ctx, 'safety-limiter', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      channelCount: 2,
      channelCountMode: 'explicit',
      processorOptions: { enabled: true, ceilingDb: -0.3 },
    });
    master.connect(lim);
    lim.connect(ctx.destination);
  } else {
    master.connect(ctx.destination);
  }

  source.start(0, startSec, durationSec);

  req.onProgress?.('Rendering', 0.1);

  /*
   * Watchdog.
   *
   * startRendering() is supposed to settle, but an OfflineAudioContext that the
   * browser declines to schedule -- which does happen once a page has
   * accumulated a number of them -- leaves a promise that never resolves, and a
   * hung await is indistinguishable from a slow render. Bound it, and clean up
   * aggressively afterwards so contexts do not accumulate in the first place.
   */
  const budgetMs = Math.max(20000, (durationSec + tailSec) * 4000);
  let rendered: AudioBuffer;
  try {
    rendered = await Promise.race([
      ctx.startRendering(),
      new Promise<never>((_, rej) =>
        setTimeout(
          () => rej(new Error(`The offline render did not complete within ${(budgetMs / 1000).toFixed(0)} s. This usually means too many audio contexts are alive; reload the page and try again.`)),
          budgetMs,
        ),
      ),
    ]);
  } finally {
    // Drop every edge so the context and its worklet processors become
    // collectable the moment this function returns.
    for (const n of [source, dacNode, ampNode, outNode, spatialNode, master]) {
      try { n.disconnect(); } catch { /* already disconnected */ }
    }
    for (const n of [dacNode, ampNode, outNode, spatialNode]) {
      n.port.onmessage = null;
      try { n.port.close(); } catch { /* not all engines implement close */ }
    }
  }
  if (req.signal?.aborted) throw new Error('Render cancelled.');
  req.onProgress?.('Rendering', 0.9);

  const channels: Float32Array[] = [];
  let peak = 0;
  for (let c = 0; c < rendered.numberOfChannels; c++) {
    const data = rendered.getChannelData(c);
    const copy = new Float32Array(data.length);
    copy.set(data);
    for (let i = 0; i < copy.length; i++) {
      const a = Math.abs(copy[i]);
      if (a > peak) peak = a;
    }
    channels.push(copy);
  }

  const quality = settingsUsedFrom(compiled);
  req.onProgress?.('Complete', 1);

  return {
    channels,
    sampleRate: rate,
    durationSec: rendered.duration,
    compiled,
    peakDbFS: 20 * Math.log10(Math.max(1e-9, peak)),
    clipped: peak > 1.0,
    settingsUsed: {
      oversample: quality.oversample,
      outputTaps: quality.outputTaps,
      realism: settings.realism,
      performance: settings.performance,
    },
    renderMs: performance.now() - t0,
  };
}

function settingsUsedFrom(c: CompiledSystem): { oversample: number; outputTaps: number } {
  return {
    oversample: Math.max(c.dac.oversample, c.amp.oversample, c.output.oversample),
    outputTaps: c.output.firTaps?.length ?? 0,
  };
}

/**
 * Measure the frequency response of the current chain (spec 56).
 *
 * Farina exponential-sweep deconvolution: play a log sweep through the chain,
 * convolve the result with the time-reversed, amplitude-corrected sweep, and
 * the linear impulse response falls out at the end of the result while the
 * harmonic distortion products separate out ahead of it. That separation is
 * what makes this method worth the trouble -- it measures response and
 * distortion in one pass without one contaminating the other.
 *
 * This is a SIMULATED measurement of the model, not a measurement of hardware,
 * and the UI labels it that way.
 */
export interface SweepMeasurement {
  freq: Float32Array;
  magDb: Float32Array;
  phaseDeg: Float32Array;
  groupDelayMs: Float32Array;
  impulse: Float32Array;
  sampleRate: number;
  /** Harmonic distortion products separated out by the deconvolution. */
  harmonicDb: Array<{ order: number; levelDb: number }>;
  thdPercent: number;
  noiseFloorDb: number;
}

export async function measureChain(
  req: Omit<OfflineRenderRequest, 'buffer'> & { seconds?: number; f1?: number; f2?: number },
): Promise<SweepMeasurement> {
  const rate = req.targetRate ?? 48000;
  const seconds = req.seconds ?? 6;
  const f1 = req.f1 ?? 10;
  const f2 = Math.min(req.f2 ?? rate / 2 - 100, rate / 2 - 1);
  const n = Math.round(seconds * rate);

  // ---- Generate the sweep and its inverse -------------------------------
  const sweep = new Float32Array(n);
  const L = Math.log(f2 / f1);
  const T = seconds;
  const K = (2 * Math.PI * f1 * T) / L;
  for (let i = 0; i < n; i++) {
    const t = i / rate;
    sweep[i] = Math.sin(K * (Math.exp((t / T) * L) - 1));
  }
  // Fade the ends so the sweep does not start or stop on a discontinuity.
  const fade = Math.round(rate * 0.02);
  for (let i = 0; i < fade; i++) {
    const w = 0.5 - 0.5 * Math.cos((Math.PI * i) / fade);
    sweep[i] *= w;
    sweep[n - 1 - i] *= w;
  }

  const buffer = new (window.OfflineAudioContext ?? OfflineAudioContext)(1, n, rate).createBuffer(2, n, rate);
  buffer.copyToChannel(sweep, 0);
  buffer.copyToChannel(sweep, 1);

  const result = await renderOffline({
    ...req,
    buffer,
    startSec: 0,
    endSec: n / rate,
    targetRate: rate,
    masterGainDb: 0,
    limiter: false,
  });

  // ---- Deconvolve --------------------------------------------------------
  // Inverse filter: time-reversed sweep with a 1/f amplitude envelope, which
  // compensates the sweep's -3 dB/octave energy tilt.
  const inv = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const t = (n - 1 - i) / rate;
    const amp = Math.exp((-t / T) * L);
    inv[i] = sweep[n - 1 - i] * amp;
  }

  const { convolveReal, getFFT, nextPow2 } = await import('../dsp/fft');
  const measured = result.channels[0];
  const full = convolveReal(measured.subarray(0, n), inv);

  // The linear impulse response sits at the end of the deconvolution.
  const irCentre = n - 1;
  const irLen = Math.min(1 << 15, n);
  const impulse = new Float32Array(irLen);
  const from = Math.max(0, irCentre - Math.round(rate * 0.005));
  impulse.set(full.subarray(from, Math.min(full.length, from + irLen)));

  // Normalise so the response reads 0 dB at 1 kHz rather than at an arbitrary level.
  let maxAbs = 0;
  for (let i = 0; i < impulse.length; i++) maxAbs = Math.max(maxAbs, Math.abs(impulse[i]));
  if (maxAbs > 0) for (let i = 0; i < impulse.length; i++) impulse[i] /= maxAbs;

  // ---- Transform to a frequency response --------------------------------
  const fftSize = nextPow2(impulse.length);
  const fft = getFFT(fftSize);
  const re = new Float32Array(fftSize);
  const im = new Float32Array(fftSize);
  re.set(impulse);
  fft.forward(re, im);

  const bins = 512;
  const freq = new Float32Array(bins);
  const magDb = new Float32Array(bins);
  const phaseDeg = new Float32Array(bins);
  const groupDelayMs = new Float32Array(bins);
  const fMin = 10;
  const fMax = rate / 2;

  let refDb = 0;
  let prevPhase = 0;
  for (let i = 0; i < bins; i++) {
    const f = fMin * Math.pow(fMax / fMin, i / (bins - 1));
    freq[i] = f;
    const b = (f / rate) * fftSize;
    const i0 = Math.min(fftSize - 2, Math.floor(b));
    const frac = b - i0;
    const r = re[i0] * (1 - frac) + re[i0 + 1] * frac;
    const m = im[i0] * (1 - frac) + im[i0 + 1] * frac;
    magDb[i] = 20 * Math.log10(Math.max(1e-9, Math.hypot(r, m)));
    let ph = Math.atan2(m, r);
    while (ph - prevPhase > Math.PI) ph -= 2 * Math.PI;
    while (prevPhase - ph > Math.PI) ph += 2 * Math.PI;
    if (i > 0) {
      const df = f - freq[i - 1];
      groupDelayMs[i] = df > 0 ? (-(ph - prevPhase) / (2 * Math.PI * df)) * 1000 : 0;
    }
    prevPhase = ph;
    phaseDeg[i] = (ph * 180) / Math.PI;
    if (Math.abs(f - 1000) < Math.abs(freq[Math.max(0, i - 1)] - 1000)) refDb = magDb[i];
  }
  for (let i = 0; i < bins; i++) magDb[i] -= refDb;
  if (bins > 1) groupDelayMs[0] = groupDelayMs[1];

  // ---- Harmonic products -------------------------------------------------
  // In a log sweep the n-th harmonic arrives T*ln(n)/ln(f2/f1) seconds EARLY.
  const harmonicDb: Array<{ order: number; levelDb: number }> = [];
  const rms = (a: Float32Array, from2: number, len: number) => {
    let s = 0;
    let count = 0;
    for (let i = from2; i < Math.min(a.length, from2 + len); i++) {
      if (i < 0) continue;
      s += a[i] * a[i];
      count++;
    }
    return count ? Math.sqrt(s / count) : 0;
  };
  const win = Math.round(rate * 0.004);
  const fundamental = rms(full, irCentre - win / 2, win) * maxAbs;
  let harmonicPower = 0;
  for (let order = 2; order <= 7; order++) {
    const advance = Math.round((T * Math.log(order) / L) * rate);
    const level = rms(full, irCentre - advance - win / 2, win) * maxAbs;
    const db = 20 * Math.log10(Math.max(1e-12, level / Math.max(1e-12, fundamental)));
    harmonicDb.push({ order, levelDb: db });
    if (db > -160) harmonicPower += Math.pow(10, db / 10);
  }

  const noiseTail = rms(full, Math.round(irCentre + rate * 0.4), win) * maxAbs;
  return {
    freq,
    magDb,
    phaseDeg,
    groupDelayMs,
    impulse,
    sampleRate: rate,
    harmonicDb,
    thdPercent: Math.sqrt(harmonicPower) * 100,
    noiseFloorDb: 20 * Math.log10(Math.max(1e-12, noiseTail / Math.max(1e-12, fundamental))),
  };
}
